import React, { useState } from 'react'
import { Badge, IconButton, Tooltip } from '@mui/material'
import { keyframes } from '@mui/system'
import { VideocamRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useVision } from './visionContext'
import { CROWD_WARNING_TEXT } from './visionState'

// C11.3: the reason of a covered screen, as the name says it after "Screen covered · ".
const COVER_REASON_CHIP_TEXT = {
  PHONE: 'phone in view',
  NO_CAMERA: 'no camera',
  NOBODY: 'nobody at the screen',
  SERVICE_UNAVAILABLE: 'vision service unavailable'
}

const PEOPLE_KEY = 'Camera in use · {{count}} people'

const RING = '#1e1e1e'

// Design v2 (Indicadores.dc.html, PO 2026-10-22): the colour of the badge of each state, and the ring that cuts it from
// the icon, the top bar's own colour, as the alarm pill does (AlarmCountPill). Not watched (v3, states 7 and 8): a grey ring
// with no fill and no pulse.
const BADGE_COLOR = { starting: '#9E9E9E', watching: '#7FE1A7', crowd: '#FF8F00', covered: '#F68D76', unwatched: RING }
const UNWATCHED_RING = '2px solid #9E9E9E'
const BADGE_TEXT_COLOR = '#121212'

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1) translate(50%, -50%); }
  50% { opacity: 0.35; transform: scale(0.8) translate(50%, -50%); }
`

// The state of the icon: not watched (C16, waiting), starting (STARTING, or covered before Sun said why), covered, too
// many people, or watching.
const kindOf = (state) => {
  if (state.waiting) {
    return 'unwatched'
  }
  if (state.covered) {
    return COVER_REASON_CHIP_TEXT[state.coverReason] ? 'covered' : 'starting'
  }
  return state.warning === 'CROWD' ? 'crowd' : 'watching'
}

const countText = (state) => (state.peopleCountSaturated ? state.peopleCount + '+' : String(state.peopleCount))

// H-7, H-8: no number until the first count nor with nobody; a saturated count is "N+" with the plural form.
const inUseText = (state, t_) => {
  if (state.peopleCount <= 0) {
    return t_('Camera in use')
  }
  if (state.peopleCountSaturated) {
    return t_(PEOPLE_KEY + '_other', { count: countText(state) })
  }
  return t_(PEOPLE_KEY, { count: state.peopleCount })
}

// The name of the icon, also its tooltip: today's texts, no new key.
const nameOf = (kind, state, t_) => {
  if (kind === 'unwatched') {
    return state.unwatchedReason === 'NO_CAMERA' ? t_('No camera') : t_('Waiting for a camera')
  }
  if (kind === 'starting') {
    return t_('Turning the camera on…')
  }
  if (kind === 'covered') {
    return `${t_('Screen covered')} · ${t_(COVER_REASON_CHIP_TEXT[state.coverReason])}`
  }
  if (kind === 'crowd') {
    // S103: a saturated count says "N+" here too, with the plural form, as the badge does.
    return state.peopleCountSaturated
      ? t_(CROWD_WARNING_TEXT + '_other', { count: countText(state), max: state.maxPeople })
      : t_(CROWD_WARNING_TEXT, { count: state.peopleCount, max: state.maxPeople })
  }
  return inUseText(state, t_)
}

// Story 213, adenda C11.3 (RN56), design v2 approved by the PO (2026-10-22, Indicadores.dc.html) — the vigilance of the
// session as one more icon of the top bar, before the bell: a camera with a badge like the bell's and the alarms', no text
// in the bar. Grey pulsing dot: starting; green dot: watching; amber number: too many people; red dot: covered. The state
// is its name and its tooltip, which pressing opens too (no hover on touch). Without a vigilance in this session nothing
// is painted, neither icon nor gap (CA10; PO 2026-10-22: also with the vision on).
const VisionChip = () => {
  const { t_ } = useSmartTranslation()
  const { state } = useVision()
  const [open, setOpen] = useState(false)

  if (!state.covered && !state.watched && !state.waiting) {
    return null
  }

  const kind = kindOf(state)
  const name = nameOf(kind, state, t_)
  const dot = kind !== 'crowd'

  return (
    <Tooltip title={name} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
      <IconButton
        data-testid='vision-chip'
        data-state={kind}
        aria-label={name}
        onClick={() => setOpen((previous) => !previous)}
        sx={{ color: '#FFFFFF' }}
      >
        <Badge
          variant={dot ? 'dot' : 'standard'}
          badgeContent={dot ? ' ' : countText(state)}
          slotProps={{
            badge: {
              'data-testid': 'vision-chip-badge',
              'data-pulse': kind === 'starting' ? 'true' : 'false',
              style: { backgroundColor: BADGE_COLOR[kind], color: BADGE_TEXT_COLOR, ...(kind === 'unwatched' ? { border: UNWATCHED_RING, boxSizing: 'border-box' } : {}) }
            }
          }}
          sx={{
            '.MuiBadge-badge': {
              boxShadow: `0 0 0 1.5px ${RING}`,
              fontWeight: 700,
              ...(dot ? { width: 10, height: 10, minWidth: 10, borderRadius: '50%' } : {}),
              ...(kind === 'starting' ? { animation: `${pulse} 1.4s ease-in-out infinite` } : {})
            }
          }}
        >
          <VideocamRounded />
        </Badge>
      </IconButton>
    </Tooltip>
  )
}

export default VisionChip
